import React, { useMemo } from 'react';

import withPDF from '../../hoc/withPDF';
import useCoverSheetOverview from '../../../hooks/useCoverSheetOverview';

import Conditional from '../../common/conditional';
import CustomUTMBreakdown from '../custom-utm/custom-utm-breakdown';
import CustomPAUTSummary from './paut-summary';
import CustomPAUTPlan from './paut-plan';
import CustomPAUTData from './paut-data';
import CustomInfoSheet from '../custom-general/custom-info-sheet';
import CustomCoverPage from '../custom-general/custom-cover-page';
import CustomUTMReportPDF from '../custom-utm/custom-utm-report';
import CustomGeneralPhotos from '../custom-general/custom-general-photos';
import GeneralDrawing from '../custom-visual-inspection/general-drawing';
import OverviewSheet from '../custom-general/overview-sheet';

import { PAUTScanPlanItem } from './pautScanPlanItems';
import { PAUTScanDataItem } from './pautScanDataItems';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface Props {
	coverData: models.ExxonMobilFVIPipingCoverData;
	drawing: models.PDFImageProps[];
	photoRecord: models.PDFImageProps[];
	projectDetails: models.Project;
	scanPlan: PAUTScanPlanItem[];
	scanData: PAUTScanDataItem[];
	tmlLocations: models.PDFImageProps[];
	utInspectionItems: models.UTMInspection[];
	utInspectionOverview: models.CustomUTMOverview;
    executiveSummary: string;
	reportTitle: string;
}

const CustomPAUTReport: React.FC<Props> = ({
	coverData,
	drawing,
	tmlLocations,
    executiveSummary,
	photoRecord,
    scanData,
	scanPlan,
	utInspectionItems,
	utInspectionOverview,
	projectDetails,
	reportTitle,
}) => {
	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice',
			value: coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '4. Work Procedure',
			value: coverData?.exxonMobilWorkProcedure,
		},
		{
			label: '5. RusselSmith Test Procedure',
			value: coverData?.russelsmithProcedure ?? '',
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [coverData.coverData]);

	const overview = useCoverSheetOverview();

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={utils.getExtractedInspectionName(projectDetails.inspectionTypeName)} />

			<CustomInfoSheet
				projectDetails={projectDetails}
				workOrderNumber={coverData.workOrderNumber}
				reportTitle={reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={projectDetails}
			/>

            <CustomPAUTSummary 
                executiveSummary={executiveSummary}
                pautDataItems={scanData}
            />

			<Conditional isVisible={utInspectionItems.length > 0}>
				<CustomUTMBreakdown
					corrosionAllowance={utInspectionOverview.corrosionAllowance}
					inspectionItems={utInspectionItems} 
				/>
			</Conditional>

            <CustomPAUTPlan pautScanItems={scanPlan} />
            
            <CustomPAUTData pautScanItems={scanData} />

			<GeneralDrawing generalDrawing={drawing} />
			
			<CustomUTMReportPDF 
				utInspectionItems={utInspectionItems}
				inspectionOverview={utInspectionOverview}
				tmlLocations={tmlLocations}
				title='UTM Location'
			/>

			<CustomGeneralPhotos label="Photographic Record" photos={photoRecord} />
		</React.Fragment>
	);
};

export default withPDF(CustomPAUTReport);
